import { getApiBase, getAdminToken } from "./serviceDeskApi";

async function parseJson(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return { ok: false, error: text || "Invalid response" };
  }
}

function authHeaders() {
  const token = getAdminToken();
  if (!token) throw new Error("Not logged in");
  return {
    Authorization: `Bearer ${token}`,
    "X-Admin-Token": token,
  };
}

async function adminGet(path) {
  const res = await fetch(`${getApiBase()}/admin/${path}`, { headers: authHeaders() });
  const data = await parseJson(res);
  if (res.status === 401) throw new Error("Session expired — log in again");
  if (!res.ok || !data.ok) throw new Error(data.error || "Request failed");
  return data.items || [];
}

async function adminUpsert(path, payload) {
  const res = await fetch(`${getApiBase()}/admin/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  if (res.status === 401) throw new Error("Session expired — log in again");
  if (!res.ok || !data.ok) throw new Error(data.error || "Save failed");
  return data;
}

async function adminDelete(path, id) {
  const q = encodeURIComponent(id);
  const res = await fetch(`${getApiBase()}/admin/${path}?id=${q}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  const data = await parseJson(res);
  if (res.status === 401) throw new Error("Session expired — log in again");
  if (!res.ok || !data.ok) throw new Error(data.error || "Delete failed");
}

export async function fetchPublicCmsData() {
  const res = await fetch(`${getApiBase()}/cms-public.php`);
  const data = await parseJson(res);
  if (!res.ok || !data.ok) throw new Error(data.error || "Could not load content");
  return data;
}

export async function submitSaleInquiryDb(payload) {
  const res = await fetch(`${getApiBase()}/cms-sale-inquiry-submit.php`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  if (!res.ok || !data.ok) throw new Error(data.error || "Could not submit inquiry");
  return data;
}

export async function submitContactForm(payload) {
  const res = await fetch(`${getApiBase()}/contact-submit.php`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  if (!res.ok || !data.ok) throw new Error(data.error || "Could not send message");
  return data;
}

export async function submitJobApplicationApi(payload) {
  const isFormData = typeof FormData !== "undefined" && payload instanceof FormData;
  const res = await fetch(`${getApiBase()}/job-application-submit.php`, {
    method: "POST",
    headers: isFormData ? undefined : { "Content-Type": "application/json" },
    body: isFormData ? payload : JSON.stringify(payload),
  });
  const data = await parseJson(res);
  if (!res.ok || !data.ok) throw new Error(data.error || "Could not submit application");
  return data;
}

export async function submitAwsActivateApplication(payload) {
  const res = await fetch(`${getApiBase()}/aws-activate-submit.php`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await parseJson(res);
  if (!res.ok || !data.ok) throw new Error(data.error || "Could not submit application");
  return data;
}

export const cmsAdminApi = {
  listBlogCategories: () => adminGet("cms-blog-categories.php"),
  saveBlogCategory: (payload) => adminUpsert("cms-blog-categories.php", payload),
  deleteBlogCategory: (id) => adminDelete("cms-blog-categories.php", id),

  listBlogs: () => adminGet("cms-blogs.php"),
  saveBlog: (payload) => adminUpsert("cms-blogs.php", payload),
  deleteBlog: (id) => adminDelete("cms-blogs.php", id),

  listJobCategories: () => adminGet("cms-job-categories.php"),
  saveJobCategory: (payload) => adminUpsert("cms-job-categories.php", payload),
  deleteJobCategory: (id) => adminDelete("cms-job-categories.php", id),

  listJobs: () => adminGet("cms-jobs.php"),
  saveJob: (payload) => adminUpsert("cms-jobs.php", payload),
  deleteJob: (id) => adminDelete("cms-jobs.php", id),

  listPartnerCategories: () => adminGet("cms-partner-categories.php"),
  savePartnerCategory: (payload) => adminUpsert("cms-partner-categories.php", payload),
  deletePartnerCategory: (id) => adminDelete("cms-partner-categories.php", id),

  listPartners: () => adminGet("cms-partners.php"),
  savePartner: (payload) => adminUpsert("cms-partners.php", payload),
  deletePartner: (id) => adminDelete("cms-partners.php", id),

  async seedPartners() {
    const res = await fetch(`${getApiBase()}/admin/cms-seed-partners.php`, {
      method: "POST",
      headers: authHeaders(),
    });
    const data = await parseJson(res);
    if (res.status === 401) throw new Error("Session expired — log in again");
    if (!res.ok || !data.ok) throw new Error(data.error || "Seed failed");
    return data;
  },

  listSaleInquiries: () => adminGet("cms-sale-inquiries.php"),
  saveSaleInquiry: (payload) => adminUpsert("cms-sale-inquiries.php", payload),
  async updateSaleInquiryStatus(id, status) {
    const res = await fetch(`${getApiBase()}/admin/cms-sale-inquiries.php`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ id, status }),
    });
    const data = await parseJson(res);
    if (res.status === 401) throw new Error("Session expired — log in again");
    if (!res.ok || !data.ok) throw new Error(data.error || "Update failed");
  },
  deleteSaleInquiry: (id) => adminDelete("cms-sale-inquiries.php", id),

  listAwsActivate: () => adminGet("cms-aws-activate.php"),
  async updateAwsActivateStatus(id, status) {
    const res = await fetch(`${getApiBase()}/admin/cms-aws-activate.php`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ id, status }),
    });
    const data = await parseJson(res);
    if (res.status === 401) throw new Error("Session expired — log in again");
    if (!res.ok || !data.ok) throw new Error(data.error || "Update failed");
  },
  deleteAwsActivate: (id) => adminDelete("cms-aws-activate.php", id),

  listContactInquiries: () => adminGet("cms-contact-inquiries.php"),
  async updateContactInquiryStatus(id, status) {
    const res = await fetch(`${getApiBase()}/admin/cms-contact-inquiries.php`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ id, status }),
    });
    const data = await parseJson(res);
    if (res.status === 401) throw new Error("Session expired — log in again");
    if (!res.ok || !data.ok) throw new Error(data.error || "Update failed");
  },
  deleteContactInquiry: (id) => adminDelete("cms-contact-inquiries.php", id),

  listJobApplications: () => adminGet("cms-job-applications.php"),
  async updateJobApplicationStatus(id, status) {
    const res = await fetch(`${getApiBase()}/admin/cms-job-applications.php`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ id, status }),
    });
    const data = await parseJson(res);
    if (res.status === 401) throw new Error("Session expired — log in again");
    if (!res.ok || !data.ok) throw new Error(data.error || "Update failed");
  },
  deleteJobApplication: (id) => adminDelete("cms-job-applications.php", id),
  jobApplicationResumeUrl(id, view = false) {
    const token = getAdminToken();
    if (!token) throw new Error("Not logged in");
    const q = encodeURIComponent(id);
    const viewParam = view ? "&view=1" : "";
    return `${getApiBase()}/admin/cms-job-application-resume.php?id=${q}&token=${encodeURIComponent(token)}${viewParam}`;
  },
};
